import React from "react";
import { Link } from "react-router-dom";
import "./privacy-policy.css";

const LAST_UPDATED = "3 November 2026";

// Replace with the actual published PulseWork policy URL.
const PULSEWORK_PDP_URL = process.env.REACT_APP_PULSEWORK_PDP_URL;

// Confirm these details before publishing.
const PRIVACY_EMAIL = "privacy@sukalpatechsolutions.com";
const ENQUIRY_EMAIL = "[Insert official STS enquiry email]";
const OFFICER_NAME = "[Insert officer's name and designation]";
const BUSINESS_ADDRESS = "[Insert official business address]";

const information = [
  "Name, email address, telephone number and other contact details.",
  "Organisation or company name and business details provided in an enquiry.",
  "The subject, contents and attachments of a submitted enquiry.",
  "Information needed to understand and respond to your request.",
  "Technical information such as browser/device details, timestamps, diagnostic information and security logs, where collected.",
];

const purposes = [
  "Review and respond to enquiries, contact requests, demo requests and purchase enquiries.",
  "Contact you to clarify requirements or provide requested information.",
  "Route enquiries to the relevant STS support, sales or business team.",
  "Maintain relevant business correspondence and follow up on your request.",
  "Operate, secure, troubleshoot and improve the website.",
  "Meet applicable legal obligations.",
];

const security = [
  "HTTPS is used to protect website traffic where enabled.",
  "Access to internal enquiry/support email accounts is limited to authorised personnel.",
  "Enquiry information is handled only to the extent needed to address the request.",
  "Reasonable safeguards are used to protect information against unauthorised access, disclosure, alteration or loss.",
  "Hosting and email providers may process information as required to provide their services.",
  "No internet-based service can be guaranteed to be completely secure.",
];

const rights = [
  ["Access", "Request access to your personal information where applicable."],
  ["Correction", "Request correction of inaccurate or incomplete information."],
  ["Erasure", "Request deletion where applicable and legally permissible."],
  ["Consent", "Withdraw consent where processing is based on consent."],
  ["Grievance", "Raise a privacy-related question, concern or complaint."],
];

function BulletList({ items }) {
  return (
    <ul className="sts-pdp-list">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function Section({ number, id, title, intro, children }) {
  return (
    <section id={id} className="sts-pdp-section">
      <div className="sts-pdp-section-heading">
        <span className="sts-pdp-number">{number}</span>
        <div>
          <h2>{title}</h2>
          {intro && <p className="sts-pdp-intro">{intro}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function InfoCard({ icon, title, children }) {
  return (
    <article className="sts-pdp-card">
      <span className="sts-pdp-card-icon" aria-hidden="true">
        {icon}
      </span>
      <div>
        <h3>{title}</h3>
        {children}
      </div>
    </article>
  );
}

export default function PrivacyPolicy() {
  return (
    <main className="sts-pdp-page">
      <div className="sts-pdp-shell">
        {/* HERO */}
        <header className="sts-pdp-hero">
          <div className="sts-pdp-hero-copy">
            <span className="sts-pdp-eyebrow">SUKALPA TECH · STS</span>

            <h1>Privacy &amp; Data Protection Policy</h1>

            <p className="sts-pdp-hero-subtitle">
              How we handle website enquiries and protect personal information.
            </p>

            <p className="sts-pdp-hero-description">
              Sukalpa Tech ("STS", "we", "us" or "our") respects your privacy.
              This policy explains what information may be received through our
              corporate website, how enquiry information is handled, and how our
              website provides access to the separate PulseWork HRMS
              application.
            </p>
          </div>

          <div className="sts-pdp-hero-art" aria-hidden="true">
            <div className="sts-pdp-art-cloud" />
            <div className="sts-pdp-art-server">
              <i />
              <i />
              <i />
            </div>
            <div className="sts-pdp-art-shield">
              <span>✓</span>
            </div>
            <div className="sts-pdp-art-lock">⌑</div>
          </div>
        </header>

        {/* SECTION NAVIGATION */}
        <nav className="sts-pdp-nav" aria-label="Privacy policy sections">
          <a href="#sts-overview">Overview</a>
          <a href="#sts-information">Information</a>
          <a href="#sts-collection">Collection</a>
          <a href="#sts-use">Purpose</a>
          <a href="#sts-enquiries">Enquiries</a>
          <a href="#sts-pulsework">PulseWork</a>
          <a href="#sts-security">Security</a>
          <a href="#sts-retention">Retention</a>
          <a href="#sts-rights">Your rights</a>
          <a href="#sts-contact">Contact</a>
        </nav>

        <div className="sts-pdp-document">
          {/* 1. OVERVIEW */}
          <Section
            number="1"
            id="sts-overview"
            title="Policy overview"
            intro="This policy applies to personal information received through the Sukalpa Tech corporate website and related website enquiries."
          >
            <div className="sts-pdp-overview-panel">
              <span className="sts-pdp-overview-icon">✓</span>

              <p>
                The STS website is a corporate website that provides information
                about our services and ways to contact our team. It also
                provides access to PulseWork, our HRMS application, which is
                embedded in the website. This policy covers the STS website and
                its enquiry handling. Personal information processed within
                PulseWork is addressed in the separate PulseWork Privacy &amp;
                Data Protection Policy.
              </p>
            </div>

            <p className="sts-pdp-muted">
              This policy should be read together with any relevant notices
              shown when you submit information or use a particular service.
            </p>
          </Section>

          {/* 2. INFORMATION */}
          <Section
            number="2"
            id="sts-information"
            title="What information may we receive?"
            intro="The information we receive depends on what you submit or how you interact with the website."
          >
            <div className="sts-pdp-two-column">
              <InfoCard icon="✉" title="Website enquiries">
                <BulletList items={information.slice(0, 4)} />
              </InfoCard>

              <InfoCard icon="⌘" title="Technical information">
                <BulletList items={information.slice(4)} />

                <p className="sts-pdp-small-note">
                  The exact technical information collected depends on the
                  website hosting, security and other tools enabled.
                </p>
              </InfoCard>
            </div>
          </Section>

          {/* 3. COLLECTION */}
          <Section
            number="3"
            id="sts-collection"
            title="How is information collected?"
            intro="Information may be provided directly by you or generated when you use the website."
          >
            <div className="sts-pdp-card-grid">
              <InfoCard icon="▣" title="Contact and enquiry forms">
                <p>
                  Details entered into contact, enquiry, demo-request, purchase
                  or similar forms.
                </p>
              </InfoCard>

              <InfoCard icon="✎" title="Direct communication">
                <p>
                  Information you send to STS by email or other contact channels
                  regarding a requested query or service.
                </p>
              </InfoCard>

              <InfoCard icon="◷" title="Website operation">
                <p>
                  Limited technical, diagnostic or security information
                  generated by the website or its service providers.
                </p>
              </InfoCard>
            </div>
          </Section>

          {/* 4. PURPOSE */}
          <Section
            number="4"
            id="sts-use"
            title="How do we use information?"
            intro="STS uses enquiry information to address the purpose for which it was submitted and to operate its website."
          >
            <div className="sts-pdp-purpose-grid">
              {purposes.map((item, index) => (
                <div className="sts-pdp-purpose-item" key={item}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <p>{item}</p>
                </div>
              ))}
            </div>

            <p className="sts-pdp-muted">
              We do not use enquiry information for unrelated purposes unless
              permitted or required by applicable law or otherwise appropriately
              authorised.
            </p>
          </Section>

          {/* 5. ENQUIRY HANDLING */}
          <Section
            number="5"
            id="sts-enquiries"
            title="How are website enquiries handled?"
            intro="Enquiry details submitted through the STS website are sent to our internal support or enquiry email address."
          >
            <div className="sts-pdp-email-flow">
              <div className="sts-pdp-flow-step">
                <span>01</span>
                <strong>You submit an enquiry</strong>
                <small>
                  You provide the details needed to describe your question or
                  request.
                </small>
              </div>

              <b aria-hidden="true">→</b>

              <div className="sts-pdp-flow-step">
                <span>02</span>
                <strong>Email is delivered</strong>
                <small>
                  The enquiry is sent to the designated STS internal
                  support/enquiry email.
                </small>
              </div>

              <b aria-hidden="true">→</b>

              <div className="sts-pdp-flow-step">
                <span>03</span>
                <strong>STS addresses your query</strong>
                <small>
                  The relevant team uses the information to review and respond
                  to your request.
                </small>
              </div>
            </div>

            <div className="sts-pdp-highlight">
              <h3>Website enquiry storage</h3>

              <p>
                STS does not store website enquiry submissions in a website
                application database. The submitted information is sent to our
                internal support or enquiry email address and is used by our
                team to address the query you requested.
              </p>

              <p>
                Email systems may retain messages, attachments and backups
                according to the email provider's systems and retention
                settings. Therefore, "no website database storage" does not mean
                that an email is immediately deleted or never retained.
              </p>
            </div>

            <p className="sts-pdp-muted">
              Please avoid including passwords, full payment-card details,
              identity documents or other highly sensitive information in a
              general enquiry form or email unless STS has provided a secure
              method and specifically requested it.
            </p>
          </Section>

          {/* 6. PULSEWORK INTEGRATION */}
          <Section
            number="6"
            id="sts-pulsework"
            title="PulseWork HRMS integration"
            intro="STS provides access to PulseWork, a workforce and human resource management application, through an embedded application experience on the STS website."
          >
            <div className="sts-pdp-pulsework-panel">
              <div className="sts-pdp-pulsework-mark">
                <span>PW</span>
              </div>

              <div className="sts-pdp-pulsework-copy">
                <span className="sts-pdp-eyebrow">
                  EMBEDDED HRMS APPLICATION
                </span>

                <h3>PulseWork</h3>

                <p>
                  PulseWork supports HRMS activities such as employee
                  administration, workforce records and enabled HR workflows.
                  When you use PulseWork, the information you provide or access
                  within the application is handled under the PulseWork Privacy
                  &amp; Data Protection Policy.
                </p>

                <a
                  href={PULSEWORK_PDP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sts-pdp-policy-link"
                >
                  Read the PulseWork Privacy Policy
                  <span aria-hidden="true">↗</span>
                </a>
              </div>
            </div>

            <div className="sts-pdp-two-column sts-pdp-integration-notes">
              <InfoCard icon="▣" title="Separate data handling">
                <p>
                  STS website enquiry information and PulseWork HRMS records are
                  separate categories of information and are handled according
                  to their respective purposes and policies.
                </p>
              </InfoCard>

              <InfoCard icon="♙" title="Customer organisation">
                <p>
                  Customer organisations determine their HRMS purposes, manage
                  authorised user access and are responsible for the information
                  they submit to PulseWork, subject to applicable law and their
                  obligations.
                </p>
              </InfoCard>
            </div>

            <p className="sts-pdp-muted">
              PulseWork may be loaded or accessed through an embedded frame. The
              application and its service providers may process technical
              information needed for sign-in, session management, security and
              application operation. Please refer to the PulseWork policy for
              details of HRMS data processing.
            </p>
          </Section>

          {/* 7. SECURITY */}
          <Section
            number="7"
            id="sts-security"
            title="How do we protect information?"
            intro="STS applies reasonable technical and organisational safeguards appropriate to the information and services involved."
          >
            <div className="sts-pdp-security-grid">
              <InfoCard icon="⌑" title="Secure transmission">
                <p>
                  HTTPS is used to protect website traffic where enabled on the
                  published website.
                </p>
              </InfoCard>

              <InfoCard icon="♙" title="Restricted access">
                <p>
                  Internal enquiry/support email access is limited to authorised
                  personnel who need the information to respond.
                </p>
              </InfoCard>

              <InfoCard icon="▤" title="Information handling">
                <p>
                  Enquiry details are handled for the requested purpose and
                  protected against unauthorised access or disclosure using
                  reasonable safeguards.
                </p>
              </InfoCard>

              <InfoCard icon="⌘" title="Service providers">
                <p>
                  Hosting and email providers may process information needed to
                  provide their services under applicable terms and safeguards.
                </p>
              </InfoCard>
            </div>

            <p className="sts-pdp-security-caveat">
              Security measures may vary according to the service and
              configuration. No online service can be guaranteed to be
              completely secure.
            </p>
          </Section>

          {/* 8. RETENTION */}
          <Section
            number="8"
            id="sts-retention"
            title="How long do we retain information?"
            intro="STS retains information only for as long as reasonably necessary for the purpose for which it was received, subject to legal and operational requirements."
          >
            <div className="sts-pdp-retention-grid">
              <div>
                <span>✉</span>
                <h3>Enquiry correspondence</h3>
                <p>
                  Enquiry messages may remain in STS email accounts while the
                  team handles the request and for reasonable business,
                  follow-up or record-keeping needs.
                </p>
              </div>

              <div>
                <span>▤</span>
                <h3>Email provider retention</h3>
                <p>
                  Email providers may retain messages or backup copies under
                  their own systems and retention settings.
                </p>
              </div>

              <div>
                <span>↻</span>
                <h3>Deletion</h3>
                <p>
                  Information may be deleted or otherwise disposed of when it is
                  no longer needed, unless retention is required or permitted by
                  law.
                </p>
              </div>
            </div>
          </Section>

          {/* 9. RIGHTS */}
          <Section
            number="9"
            id="sts-rights"
            title="Your rights and choices"
            intro="Depending on applicable law and the circumstances of processing, you may have rights relating to your personal information."
          >
            <div className="sts-pdp-rights-grid">
              {rights.map(([title, text]) => (
                <div className="sts-pdp-right" key={title}>
                  <b aria-hidden="true">✓</b>
                  <span>
                    <strong>{title}</strong>
                    <small>{text}</small>
                  </span>
                </div>
              ))}
            </div>

            <p className="sts-pdp-muted">
              We may need to verify your identity before responding. Some
              requests may be limited where information must be retained to
              comply with law, resolve a matter or protect the rights of others.
            </p>
          </Section>

          {/* 10. CONTACT */}
          <Section
            number="10"
            id="sts-contact"
            title="Contact and grievance redressal"
            intro="For questions, requests or complaints about this policy or STS website privacy practices, contact us using the details below."
          >
            <div className="sts-pdp-contact-card">
              <div className="sts-pdp-contact-main">
                <span className="sts-pdp-contact-symbol">✉</span>

                <div>
                  <h3>Sukalpa Tech</h3>
                  <p>Privacy and Data Protection Contact</p>

                  <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>

                  <p className="sts-pdp-contact-secondary">
                    Enquiry/support email: {ENQUIRY_EMAIL}
                  </p>
                </div>
              </div>

              <div className="sts-pdp-contact-divider" />

              <div className="sts-pdp-contact-officer">
                <strong>Privacy / Grievance Officer</strong>
                <span>{OFFICER_NAME}</span>
                <span>{BUSINESS_ADDRESS}</span>
              </div>
            </div>

            <p className="sts-pdp-muted">
              Replace the contact placeholders with confirmed official details
              before publishing.
            </p>
          </Section>

          {/* ADDITIONAL INFORMATION */}
          <section className="sts-pdp-additional">
            <h2>Additional information</h2>

            <div className="sts-pdp-additional-grid">
              <article>
                <h3>Sharing information</h3>
                <p>
                  STS may share information with authorised team members and
                  relevant hosting, email or service providers where needed to
                  handle an enquiry, operate the website or provide a requested
                  service. Information may also be disclosed where required by
                  law or to protect rights and security.
                </p>
              </article>

              <article>
                <h3>Cookies and similar technologies</h3>
                <p>
                  The website or its embedded services may use essential
                  cookies, session technologies or similar tools for
                  functionality and security. Any analytics or non-essential
                  tracking should be described according to the tools actually
                  enabled.
                </p>
              </article>

              <article>
                <h3>Third-party services</h3>
                <p>
                  The website may link to or embed third-party services,
                  including PulseWork. Their processing may be governed by their
                  own policies and terms.
                </p>
              </article>

              <article>
                <h3>Policy updates</h3>
                <p>
                  We may update this policy when our website, services,
                  information handling or legal requirements change. The current
                  version and revision date will be shown on this page.
                </p>
              </article>
            </div>
          </section>

          {/* FOOTER */}
          <footer className="sts-pdp-footer">
            <p>
              ⓘ This policy is reviewed periodically and may be updated to
              reflect changes to our website, services or applicable
              requirements.
            </p>

            <span>Last updated: {LAST_UPDATED}</span>

            <button
              type="button"
              onClick={() => window.print()}
              className="sts-pdp-print-button"
            >
              Print / Save as PDF
            </button>
          </footer>
        </div>

        <div className="sts-pdp-bottom-footer">
          <span>© 2022 Sukalpa Tech. All Rights Reserved.</span>

          <Link to="/">Back to STS website</Link>
        </div>
      </div>
    </main>
  );
}
